import { z } from 'zod';

/**
 * Психологические тесты профиля знакомств (3 штуки).
 *
 * Источник истины — легаси `profile.html` (вопросы, варианты, логика скоринга).
 * Каталог вопросов + чистые функции скоринга живут здесь (в contracts), чтобы
 * шариться фронтом (экран прохождения) и сервером (надёжный подсчёт результата —
 * в отличие от легаси, где считалось на клиенте).
 *
 * Тесты:
 * - `socionics` — 4 вопроса-дихотомии (E/I, S/N, T/F, J/P) → склейка 4 букв (`INTJ`).
 * - `psychotype` — 7 вопросов по 7 радикалов → доминирующий радикал.
 * - `archetype` — 5 вопросов по 8 архетипов → доминирующий архетип.
 */

export const PSYCH_TEST_TYPES = ['socionics', 'psychotype', 'archetype'] as const;
export const PsychTestType = z.enum(PSYCH_TEST_TYPES);
export type PsychTestType = z.infer<typeof PsychTestType>;

export interface PsychQuestionOption {
  readonly text: string;
  /** Код, который вносит вклад в результат (буква дихотомии / радикал / архетип). */
  readonly value: string;
}
export interface PsychQuestion {
  readonly question: string;
  readonly options: ReadonlyArray<PsychQuestionOption>;
}
export interface PsychTestDef {
  readonly type: PsychTestType;
  readonly title: string;
  readonly emoji: string;
  readonly questions: ReadonlyArray<PsychQuestion>;
}

// ===== Социотип (4 вопроса, дихотомии) =====

// Порядок вопросов = порядок дихотомий (E/I → S/N → T/F → J/P): склейка ответов даёт код вроде INTJ.
const SOCIONICS: PsychTestDef = {
  type: 'socionics',
  title: 'Социотип',
  emoji: '🧠',
  questions: [
    {
      question: 'Как вы восстанавливаете энергию?',
      options: [
        { text: 'В общении с людьми, на мероприятиях', value: 'E' },
        { text: 'В одиночестве, в тишине', value: 'I' },
      ],
    },
    {
      question: 'Что для вас важнее?',
      options: [
        { text: 'Конкретные факты и детали', value: 'S' },
        { text: 'Идеи и возможности', value: 'N' },
      ],
    },
    {
      question: 'Как вы принимаете решения?',
      options: [
        { text: 'На основе логики и объективности', value: 'T' },
        { text: 'На основе чувств и ценностей', value: 'F' },
      ],
    },
    {
      question: 'Как вы предпочитаете жить?',
      options: [
        { text: 'По плану, структурированно', value: 'J' },
        { text: 'Гибко, спонтанно', value: 'P' },
      ],
    },
  ],
};

/** Названия 16 социотипов по коду. */
export const SOCIONICS_NAMES: Record<string, string> = {
  INTJ: 'Аналитик (INTJ)',
  INTP: 'Мыслитель (INTP)',
  ENTJ: 'Командир (ENTJ)',
  ENTP: 'Дебатер (ENTP)',
  INFJ: 'Адвокат (INFJ)',
  INFP: 'Посредник (INFP)',
  ENFJ: 'Тренер (ENFJ)',
  ENFP: 'Борец (ENFP)',
  ISTJ: 'Администратор (ISTJ)',
  ISFJ: 'Защитник (ISFJ)',
  ESTJ: 'Менеджер (ESTJ)',
  ESFJ: 'Консул (ESFJ)',
  ISTP: 'Виртуоз (ISTP)',
  ISFP: 'Художник (ISFP)',
  ESTP: 'Предприниматель (ESTP)',
  ESFP: 'Развлекатель (ESFP)',
};

// ===== Психотип (7 вопросов, 7 радикалов) =====

export const PSYCHOTYPE_CODES = [
  'hysteroid',
  'schizoid',
  'epileptoid',
  'emotive',
  'paranoid',
  'hyperthymic',
  'anxious',
] as const;

export const PSYCHOTYPE_NAMES: Record<string, string> = {
  hysteroid: 'Истероидный',
  schizoid: 'Шизоидный',
  epileptoid: 'Эпилептоидный',
  emotive: 'Эмотивный',
  paranoid: 'Паранойяльный',
  hyperthymic: 'Гипертимный',
  anxious: 'Тревожный',
};

const PSYCHOTYPE_OPTION_TEXT: Record<string, string[]> = {
  hysteroid: [
    'Люблю быть в центре внимания',
    'Очень болезненно, могу обидеться',
    'Внимание и восхищение',
    'Импульсивно, под влиянием эмоций',
    'Ищу поддержку и внимание других',
    'Признание и похвала',
    'Драматизирую, привлекаю внимание',
  ],
  schizoid: [
    'Предпочитаю наблюдать со стороны',
    'Спокойно, анализирую объективно',
    'Личное пространство и свобода',
    'Рационально, после долгого размышления',
    'Ухожу в себя, изолируюсь',
    'Интеллектуальный интерес',
    'Избегаю, ухожу от конфликта',
  ],
  epileptoid: [
    'Стремлюсь к порядку и структуре',
    'Агрессивно, защищаю свою позицию',
    'Порядок и предсказуемость',
    'Быстро, по чёткому плану',
    'Контролирую ситуацию, организую',
    'Дисциплина и порядок',
    'Агрессивно отстаиваю свою позицию',
  ],
  emotive: [
    'Очень чувствителен к эмоциям других',
    'Эмоционально, переживаю глубоко',
    'Эмоциональная близость и понимание',
    'Интуитивно, сердцем',
    'Выражаю эмоции, плачу или злюсь',
    'Эмоциональная связь с людьми',
    'Стремлюсь к компромиссу и пониманию',
  ],
  paranoid: [
    'Всегда ищу новые идеи и проекты',
    'Игнорирую, если не согласен',
    'Общие цели и идеи',
    'Стратегически, с далеко идущими целями',
    'Фокусируюсь на решении проблемы',
    'Великие идеи и миссия',
    'Упорно доказываю свою правоту',
  ],
  hyperthymic: [
    'Очень активен и энергичен',
    'Легко, не принимаю близко к сердцу',
    'Веселье и активность',
    'Спонтанно, по ситуации',
    'Остаюсь оптимистичным, ищу позитив',
    'Новые впечатления и приключения',
    'Легко переключаюсь, не зацикливаюсь',
  ],
  anxious: [
    'Склонен к тревоге и переживаниям',
    'Тревожно, начинаю сомневаться в себе',
    'Безопасность и поддержка',
    'Осторожно, взвешивая все риски',
    'Паникую, переживаю, не могу успокоиться',
    'Избегание проблем и конфликтов',
    'Уступаю, чтобы избежать конфликта',
  ],
};

const PSYCHOTYPE_QUESTION_TITLES = [
  'Как вы ведёте себя в компании?',
  'Как вы реагируете на критику?',
  'Что для вас важнее в отношениях?',
  'Как вы принимаете решения?',
  'Как вы справляетесь со стрессом?',
  'Что вас больше всего мотивирует?',
  'Как вы ведёте себя в конфликте?',
];

const PSYCHOTYPE: PsychTestDef = {
  type: 'psychotype',
  title: 'Психотип',
  emoji: '⚡',
  questions: PSYCHOTYPE_QUESTION_TITLES.map((question, qi) => ({
    question,
    options: PSYCHOTYPE_CODES.map((code) => ({
      text: PSYCHOTYPE_OPTION_TEXT[code]?.[qi] ?? '',
      value: code,
    })),
  })),
};

// ===== Архетип (5 вопросов, 8 архетипов) =====

export const ARCHETYPE_CODES = [
  'caregiver',
  'creator',
  'hero',
  'sage',
  'explorer',
  'lover',
  'ruler',
  'innocent',
] as const;

export const ARCHETYPE_NAMES: Record<string, string> = {
  caregiver: 'Опекун',
  creator: 'Творец',
  hero: 'Герой',
  sage: 'Мудрец',
  explorer: 'Искатель',
  lover: 'Любящий',
  ruler: 'Правитель',
  innocent: 'Невинный',
};

const ARCHETYPE_OPTION_TEXT: Record<string, string[]> = {
  caregiver: [
    'Помощь и забота о других',
    'Защитник и опекун',
    'Благополучие близких',
    'Забочусь о других, чтобы забыть о проблемах',
    'Заботу и помощь',
  ],
  creator: [
    'Создание нового и творчество',
    'Творец и художник',
    'Самовыражение через творчество',
    'Создаю что-то новое',
    'Красоту и искусство',
  ],
  hero: [
    'Преодоление препятствий и достижение целей',
    'Воин и победитель',
    'Победа и достижения',
    'Борюсь и преодолеваю',
    'Мужество и силу',
  ],
  sage: [
    'Поиск истины и знаний',
    'Учитель и мудрец',
    'Понимание и мудрость',
    'Изучаю и анализирую',
    'Знания и истину',
  ],
  explorer: [
    'Свобода и новые впечатления',
    'Искатель и путешественник',
    'Свобода и независимость',
    'Ищу новые пути и возможности',
    'Приключения и опыт',
  ],
  lover: [
    'Любовь и близость',
    'Любящий и страстный',
    'Любовь и отношения',
    'Ищу поддержку в отношениях',
    'Любовь и страсть',
  ],
  ruler: [
    'Власть и влияние',
    'Лидер и правитель',
    'Контроль и порядок',
    'Беру контроль в свои руки',
    'Власть и успех',
  ],
  innocent: [
    'Веселье и радость',
    'Ребёнок и оптимист',
    'Счастье и радость',
    'Сохраняю оптимизм',
    'Радость и простоту',
  ],
};

const ARCHETYPE_QUESTION_TITLES = [
  'Что вас больше всего мотивирует?',
  'Как вы видите свою роль в жизни?',
  'Что для вас самое важное?',
  'Как вы справляетесь с трудностями?',
  'Что вы цените больше всего?',
];

const ARCHETYPE: PsychTestDef = {
  type: 'archetype',
  title: 'Архетип',
  emoji: '🎭',
  questions: ARCHETYPE_QUESTION_TITLES.map((question, qi) => ({
    question,
    options: ARCHETYPE_CODES.map((code) => ({
      text: ARCHETYPE_OPTION_TEXT[code]?.[qi] ?? '',
      value: code,
    })),
  })),
};

/** Каталог всех тестов по типу. */
export const PSYCH_TESTS: Record<PsychTestType, PsychTestDef> = {
  socionics: SOCIONICS,
  psychotype: PSYCHOTYPE,
  archetype: ARCHETYPE,
};

/** Каталог в виде массива в каноническом порядке — для UI-списка. */
export const PSYCH_TEST_CATALOG: ReadonlyArray<PsychTestDef> = PSYCH_TEST_TYPES.map((t) => PSYCH_TESTS[t]);

/** Сколько вопросов в тесте. */
export function psychTestQuestionCount(type: PsychTestType): number {
  return PSYCH_TESTS[type].questions.length;
}

// ===== Скоринг =====

/** Сырой результат одного теста (хранится в `NetworkingProfile.testResults`). */
export const PsychTestResult = z.object({
  /** Код результата: `INTJ` | `emotive` | `sage`. */
  type: z.string(),
  /** Человекочитаемое название: `Аналитик (INTJ)` | `Эмотивный` | `Мудрец`. */
  label: z.string(),
  /** Сырые ответы по порядку вопросов (выбранные value). */
  answers: z.array(z.string()),
  /** Подсчёт голосов по кодам (для psychotype/archetype; у socionics отсутствует). */
  counts: z.record(z.string(), z.number()).optional(),
});
export type PsychTestResult = z.infer<typeof PsychTestResult>;

/** Агрегат всех тестов профиля (хранится как `testResults` Json). */
export const PsychTestResults = z.object({
  socionics: PsychTestResult.optional(),
  psychotype: PsychTestResult.optional(),
  archetype: PsychTestResult.optional(),
});
export type PsychTestResults = z.infer<typeof PsychTestResults>;

/** Краткий результат для показа в профиле/анкете (без сырых ответов). */
export const PsychTestSummary = z.object({
  type: z.string(),
  label: z.string(),
});
export type PsychTestSummary = z.infer<typeof PsychTestSummary>;

export const PsychTestsSummaryDto = z.object({
  socionics: PsychTestSummary.nullable(),
  psychotype: PsychTestSummary.nullable(),
  archetype: PsychTestSummary.nullable(),
});
export type PsychTestsSummaryDto = z.infer<typeof PsychTestsSummaryDto>;

/** Тело запроса на сохранение результата: только ответы, скоринг — на сервере. */
export const PsychTestSubmitInput = z.object({
  answers: z.array(z.string()).min(1),
});
export type PsychTestSubmitInput = z.infer<typeof PsychTestSubmitInput>;

/** Безопасный резолв названия результата по типу теста и коду. */
export function psychTestLabel(type: PsychTestType, code: string): string {
  const map =
    type === 'socionics' ? SOCIONICS_NAMES : type === 'psychotype' ? PSYCHOTYPE_NAMES : ARCHETYPE_NAMES;
  return map[code] ?? code;
}

/**
 * Проверяет, что ответы валидны для теста: длина = числу вопросов и каждый
 * выбранный value присутствует среди вариантов соответствующего вопроса.
 */
export function validatePsychAnswers(type: PsychTestType, answers: string[]): boolean {
  const def = PSYCH_TESTS[type];
  if (answers.length !== def.questions.length) return false;
  return answers.every((value, qi) => def.questions[qi]?.options.some((opt) => opt.value === value));
}

/**
 * Считает результат теста по ответам. Бросает, если ответы невалидны
 * (вызывающий должен сначала вызвать `validatePsychAnswers`).
 *
 * - socionics: склейка букв в порядке дихотомий.
 * - psychotype/archetype: доминирующий код; при равенстве — раньше в каноне.
 */
export function scorePsychTest(type: PsychTestType, answers: string[]): PsychTestResult {
  if (!validatePsychAnswers(type, answers)) {
    throw new Error(`Invalid answers for psych test "${type}"`);
  }

  if (type === 'socionics') {
    // По одной букве на дихотомию (порядок answers = порядок вопросов) — склеиваем в код INTJ.
    const code = answers.join('');
    return { type: code, label: psychTestLabel('socionics', code), answers };
  }

  // psychotype / archetype: голосование по кодам.
  const order: ReadonlyArray<string> = type === 'psychotype' ? PSYCHOTYPE_CODES : ARCHETYPE_CODES;
  const counts: Record<string, number> = {};
  for (const value of answers) counts[value] = (counts[value] ?? 0) + 1;

  // Доминирующий код: максимум голосов, при равенстве — раньше в каноническом порядке.
  let winner = order[0] ?? '';
  for (const code of order) {
    if ((counts[code] ?? 0) > (counts[winner] ?? 0)) winner = code;
  }

  return { type: winner, label: psychTestLabel(type, winner), answers, counts };
}

/** Превращает сырой результат в краткую сводку (type + label). */
export function toPsychTestSummary(result: PsychTestResult | undefined | null): PsychTestSummary | null {
  if (!result) return null;
  return { type: result.type, label: result.label };
}

/** Голоса по радикалам из семи ответов (порядок вопросов не важен — важен состав). */
export function psychotypeCountsFromAnswers(answers: ReadonlyArray<string>): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const value of answers) counts[value] = (counts[value] ?? 0) + 1;
  return counts;
}

/**
 * Полный порядок 7 радикалов: больше голосов выше; при равенстве — канон
 * `PSYCHOTYPE_CODES` (не алфавит). Не выкидывает нулевые: публичный ритм всегда
 * из семи элементов, если тест пройден.
 */
export function rankPsychotypeCodes(counts: Readonly<Record<string, number>>): string[] {
  return [...PSYCHOTYPE_CODES].sort((a, b) => {
    const diff = (counts[b] ?? 0) - (counts[a] ?? 0);
    if (diff !== 0) return diff;
    return PSYCHOTYPE_CODES.indexOf(a) - PSYCHOTYPE_CODES.indexOf(b);
  });
}

/** Вопрос единого квиза с привязкой к исходной методике (для скоринга). */
export interface TaggedPsychQuestion {
  readonly source: PsychTestType;
  readonly questionIndex: number;
  readonly question: string;
  readonly options: ReadonlyArray<PsychQuestionOption>;
}

export const PSYCH_CODE_QUESTION_COUNT = PSYCH_TEST_TYPES.reduce(
  (sum, type) => sum + PSYCH_TESTS[type].questions.length,
  0,
);

export function flattenPsychCodeQuestions(): TaggedPsychQuestion[] {
  return PSYCH_TEST_TYPES.flatMap((source) =>
    PSYCH_TESTS[source].questions.map((item, questionIndex) => ({
      source,
      questionIndex,
      question: item.question,
      options: item.options,
    })),
  );
}

function shuffleArray<T>(items: readonly T[], rng: () => number = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const current = out[i];
    const swap = out[j];
    if (current === undefined || swap === undefined) continue;
    out[i] = swap;
    out[j] = current;
  }
  return out;
}

/** rng, при котором Fisher-Yates не меняет порядок (j === i). Для спеков. */
export function identityShuffleRng(): number {
  return 0.999999999;
}

export function shufflePsychCodeDeck(rng: () => number = Math.random): TaggedPsychQuestion[] {
  return shuffleArray(flattenPsychCodeQuestions(), rng).map((item) => ({
    source: item.source,
    questionIndex: item.questionIndex,
    question: item.question,
    options: shuffleArray(item.options, rng),
  }));
}

export function collectPsychCodeAnswers(
  answers: ReadonlyArray<{ source: PsychTestType; questionIndex: number; value: string }>,
): Record<PsychTestType, string[]> {
  const buckets: Record<PsychTestType, string[]> = {
    socionics: Array.from({ length: psychTestQuestionCount('socionics') }, () => ''),
    psychotype: Array.from({ length: psychTestQuestionCount('psychotype') }, () => ''),
    archetype: Array.from({ length: psychTestQuestionCount('archetype') }, () => ''),
  };
  for (const item of answers) {
    const bucket = buckets[item.source];
    if (item.questionIndex < 0 || item.questionIndex >= bucket.length) continue;
    bucket[item.questionIndex] = item.value;
  }
  return buckets;
}
